import { forwardRef } from "react";
import { classNames } from "@NBCUDTC/reactjs-utility-belt/classnames";
import { Icon } from "@NBCUDTC/reactjs-utility-belt/components/atoms/Icon";

import type { MobileBottomNavItemProps } from "./types";
import styles from "./MobileBottomNavItem.module.scss";

/**
 * Mobile Bottom Nav Item that renders a single navigation item with icon
 *
 * @param item - The navigation item data including icon names and label
 * @param selected - Whether the item is currently selected
 * @param onClick - Click event handler for the item
 * @param className - Additional class names for styling
 * @param attrs - Additional HTML attributes for the button element
 * @returns the MobileBottomNavItem component
 *
 * @example
 * ```tsx
 * <MobileBottomNavItem item={item} selected={true} onClick={() => console.log('Clicked!')} />
 * ```
 */
const MobileBottomNavItem = forwardRef<
  HTMLButtonElement,
  MobileBottomNavItemProps
>(({ item, selected, onClick, className, ...attrs }, ref) => {
  const { iconName, selectedIconName, label } = item;
  const cn = classNames(
    styles["nav-item"],
    selected && styles["selected"],
    className,
  );

  return (
    <button
      className={cn}
      ref={ref}
      onClick={onClick}
      aria-label={label}
      {...attrs}
    >
      <Icon
        icon={selected && selectedIconName ? selectedIconName : iconName}
        className={styles["nav-icon"]}
      />
    </button>
  );
});

MobileBottomNavItem.displayName = "MobileBottomNavItem";

export { MobileBottomNavItem };
